import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, Loader2, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { AdminHeader, Badge, Field, Panel, borderStyle, inputCls } from "../ui";

// Warnings that better copy (or a number the owner knows) can actually clear.
const FIXABLE = ["description_generated", "description_thin", "title_no_vehicle",
  "fitment_missing", "qty_unconfirmed", "weight_missing"];

const codes = (p) => (p.merchant_issues || []).map((i) => i.code);
const needsWork = (p) => codes(p).some((c) => FIXABLE.includes(c));

function ProductRow({ product, onSaved }) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const issues = codes(product);

  async function makeDraft() {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/catalog/products/${product.id}/draft-copy`);
      setDraft({
        ...data.draft,
        weight_kg: product.weight_grams ? (product.weight_grams / 1000).toString() : "",
        qty: String(product.qty ?? 0),
      });
      if (data.draft.needs_fitment) {
        toast.warning("Fitment unknown", {
          description: `${product.title || product.name}: nothing in the data says which vehicle it fits - add it yourself.`,
        });
      }
    } catch (err) {
      toast.error("Could not draft copy", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    setSaving(true);
    try {
      const weight = parseFloat(draft.weight_kg);
      await api.patch(`/admin/catalog/products/${product.id}`, {
        title: draft.title.trim(),
        description: draft.description.trim(),
        fitment_text: draft.fitment_text.trim(),
        qty: parseInt(draft.qty, 10) || 0,
        weight_grams: Number.isFinite(weight) && weight > 0 ? Math.round(weight * 1000) : null,
        description_generated: false,
      });
      toast.success("Listing updated", { description: draft.title.slice(0, 60) });
      setDraft(null);
      onSaved();
    } catch (err) {
      toast.error("Could not save", { description: err?.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Panel data-testid="polish-row">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-medium" data-testid="polish-current-title">{product.title || product.name}</p>
          <p className="mt-0.5 text-xs text-[color:var(--nq-stone)]">
            {product.sku}{product.brand ? ` · ${product.brand}` : ""}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {issues.filter((c) => FIXABLE.includes(c)).map((c) => (
              <Badge key={c} tone="gold">{c.replace(/_/g, " ")}</Badge>
            ))}
          </div>
        </div>
        <button
          onClick={makeDraft}
          disabled={busy}
          className="btn-ghost inline-flex items-center gap-2 text-xs"
          data-testid="polish-draft-btn"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
          {draft ? "Draft again" : "Draft with AI"}
        </button>
      </div>

      {draft && (
        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <div className="rounded-lg border p-4" style={{ ...borderStyle, background: "rgba(255,255,255,0.02)" }}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[color:var(--nq-stone)]">
              Now
            </p>
            <p className="text-sm">{product.title || product.name}</p>
            <p className="mt-2 whitespace-pre-line text-xs text-[color:var(--nq-stone)]">
              {product.description || "(no description)"}
            </p>
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider gold-text">Proposed</p>
            <Field label="Title">
              <input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                className={inputCls}
                data-testid="polish-title"
              />
            </Field>
            <Field label="Description">
              <textarea
                rows={5}
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                className={inputCls}
                data-testid="polish-description"
              />
            </Field>
            <Field label="Fits which vehicles?" hint="Leave blank if you genuinely don't know - never guess.">
              <input
                value={draft.fitment_text}
                onChange={(e) => setDraft({ ...draft, fitment_text: e.target.value })}
                className={inputCls}
                data-testid="polish-fitment"
              />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Weight (kg)" hint="Clears the freight warning.">
                <input
                  value={draft.weight_kg}
                  onChange={(e) => setDraft({ ...draft, weight_kg: e.target.value })}
                  inputMode="decimal"
                  className={inputCls}
                  data-testid="polish-weight"
                />
              </Field>
              <Field label="Confirm quantity on hand" hint="Counted, not guessed from the old site.">
                <input
                  value={draft.qty}
                  onChange={(e) => setDraft({ ...draft, qty: e.target.value })}
                  inputMode="numeric"
                  className={inputCls}
                  data-testid="polish-qty"
                />
              </Field>
            </div>
            <div className="flex gap-2">
              <button
                onClick={apply}
                disabled={saving}
                className="btn-gold inline-flex items-center gap-2 text-sm"
                data-testid="polish-apply"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Apply to listing
              </button>
              <button onClick={() => setDraft(null)} className="btn-ghost text-sm" data-testid="polish-discard">
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}

export default function ContentPolish() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/catalog/products", { params: { limit: 500 } });
      setProducts(data.products || []);
    } catch (err) {
      toast.error("Could not load products", { description: err?.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const outstanding = useMemo(() => products.filter(needsWork), [products]);
  const clean = products.length - outstanding.length;

  return (
    <div className="space-y-6">
      <AdminHeader
        title="Content polish"
        subtitle="Vehicle-led titles and real descriptions - what Google matches searches against"
      />

      <Panel>
        <p className="flex items-center gap-2 text-sm">
          <Sparkles className="h-4 w-4 gold-text" />
          <strong data-testid="polish-outstanding">{outstanding.length}</strong> listings still need work
          {clean > 0 && <span className="text-[color:var(--nq-stone)]"> · {clean} already clean</span>}
        </p>
        <p className="mt-2 flex gap-2 text-xs text-[color:var(--nq-stone)]">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
          The AI drafts from what we already know about the part and will never invent a vehicle
          fitment - if it can't tell what the part suits, it says so and leaves fitment blank for you.
          Nothing goes live until you press Apply.
        </p>
      </Panel>

      {loading ? (
        <Panel><p className="flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</p></Panel>
      ) : outstanding.length === 0 ? (
        <Panel><p className="text-sm text-emerald-400">Every listing's copy is clean. Nothing to polish.</p></Panel>
      ) : (
        outstanding.map((p) => <ProductRow key={p.id} product={p} onSaved={load} />)
      )}
    </div>
  );
}
